import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const UserTable = ({ users, type, onViewProfile, onSuspend, onActivate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'pending', label: 'Pending' },
    { value: 'suspended', label: 'Suspended' }
  ];

  const sortOptions = [
    { value: 'date', label: 'Date Joined' },
    { value: 'name', label: 'Name' },
    { value: 'activity', label: 'Last Activity' }
  ];

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-success/10 text-success',
      pending: 'bg-warning/10 text-warning',
      suspended: 'bg-error/10 text-error'
    };
    return badges?.[status] || 'bg-muted text-muted-foreground';
  };

  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesStatus = statusFilter === 'all' || user?.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="bg-card rounded-2xl p-4 md:p-6 elevation-sm">
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <div className="flex-1">
          <Input
            type="search"
            placeholder={`Search ${type}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full"
          />
        </div>
        <div className="flex gap-2">
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Filter by status"
            className="w-full sm:w-40"
          />
          <Select
            options={sortOptions}
            value={sortBy}
            onChange={setSortBy}
            placeholder="Sort by"
            className="w-full sm:w-40"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2 text-sm font-semibold text-foreground">User</th>
              <th className="text-left py-3 px-2 text-sm font-semibold text-foreground hidden md:table-cell">Status</th>
              <th className="text-left py-3 px-2 text-sm font-semibold text-foreground hidden lg:table-cell">Joined</th>
              <th className="text-left py-3 px-2 text-sm font-semibold text-foreground hidden lg:table-cell">Activity</th>
              <th className="text-right py-3 px-2 text-sm font-semibold text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers?.map((user) => (
              <tr key={user?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                <td className="py-4 px-2">
                  <div className="flex items-center gap-3">
                    <Image 
                      src={user?.avatar} 
                      alt={user?.avatarAlt}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground line-clamp-1">{user?.name}</p>
                      <p className="text-xs text-muted-foreground line-clamp-1">{user?.email}</p>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-2 hidden md:table-cell">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusBadge(user?.status)}`}>
                    {user?.status}
                  </span>
                </td>
                <td className="py-4 px-2 hidden lg:table-cell">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">{user?.joinedDate}</span>
                </td>
                <td className="py-4 px-2 hidden lg:table-cell">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">{user?.lastActivity}</span>
                </td>
                <td className="py-4 px-2">
                  <div className="flex items-center justify-end gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      iconName="FileText"
                      onClick={() => onViewProfile(user)}
                    >
                      <span className="hidden sm:inline">View</span>
                    </Button>
                    {user?.status === 'active' ? (
                      <Button 
                        variant="destructive" 
                        size="sm" 
                        iconName="Ban"
                        onClick={() => onSuspend(user)}
                      >
                        <span className="hidden sm:inline">Suspend</span>
                      </Button>
                    ) : (
                      <Button 
                        variant="success" 
                        size="sm" 
                        iconName="Check"
                        onClick={() => onActivate(user)}
                      >
                        <span className="hidden sm:inline">Activate</span>
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filteredUsers?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No {type} found matching your criteria</p>
        </div>
      )}
    </div>
  );
};

export default UserTable;